import { Button } from '@distribusion/owl-frontend-ui'
import React, { ReactElement } from 'react'

import { POPUP_TYPES } from '@constants/popupTypes'
import { usePopupAnalytics } from '@hooks/usePopupAnalytics'
import success from '@images/common/success.svg'
import errorImage from '@images/reservations/error.png'
import { useTranslation } from '@lib/i18n'
import { ReservationErrorType } from '@pages/Checkout/hooks/useReservationErrors'
import ReservationErrorModal from '@pages/Checkout/ReservationsErrorModal'
import Default from '@pages/Checkout/ReservationsErrorModal/Footer/Default'

interface ReservationErrorsProps {
  type: ReservationErrorType | null
  onClose: () => void
}

type ErrorType = Exclude<ReservationErrorType, 'confirmError'>

interface Config {
  title: string
  body: string
  footer?: ReactElement | null
  iconPath?: string
}

const ReservationErrorHandler = ({ type, onClose }: ReservationErrorsProps): ReactElement => {
  const { t } = useTranslation()

  const { trackPopupClick } = usePopupAnalytics(POPUP_TYPES.RESERVATION_ERROR, 'checkout', !!type)

  const closeCTALabel = t('checkout.reservationModal.close')

  const handleClose = () => {
    trackPopupClick(closeCTALabel)
    onClose()
  }

  const config: Record<ErrorType, Config> = {
    booking: { title: t('checkout.reservationModal.errorHeader'), body: t('checkout.reservationBookingError') },
    createError: {
      body: t('checkout.reservationModal.createErrorBody'),
      title: t('checkout.reservationModal.createErrorHeader'),
    },
    shopper: {
      body: t('checkout.reservationModal.shopperErrorBody'),
      title: t('checkout.reservationModal.shopperErrorHeader'),
      footer: (
        <Button data-tag="reservation-failed-close-button" onClick={handleClose}>
          {closeCTALabel}
        </Button>
      ),
    },
    promocode: {
      body: t('checkout.reservationModal.promocodeErrorBody'),
      title: t('checkout.reservationModal.promocodeErrorHeader'),
      footer: (
        <Button data-tag="reservation-failed-close-button" onClick={handleClose}>
          {closeCTALabel}
        </Button>
      ),
    },
    alreadyConfirmed: {
      body: t('checkout.reservationConfirmed.description'),
      title: t('checkout.reservationConfirmed.title'),
      footer: null,
      iconPath: success,
    },
  }

  const props = type ? config[type as keyof typeof config] : null

  return <ReservationErrorModal isOpened={!!props} footer={<Default />} iconPath={errorImage} {...(props as Config)} />
}

export default ReservationErrorHandler
